import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import LoadError from '@/components/ui/LoadError.tsx';
import useGetExpense from '@/features/dashboard/expenses/hooks/useGetExpense.ts';
import useUpdateExpense from '@/features/dashboard/expenses/hooks/useUpdateExpense.ts';
import {
  expenseSchema,
  ExpenseFormData,
  ExpenseInputData,
} from '@/features/dashboard/expenses/schemas/expenseSchema.ts';
import { expenseMapper } from '@/features/dashboard/expenses/types/expenseMapper.ts';
import ExpenseForm from '@/features/dashboard/expenses/components/ExpenseForm.tsx';
import ExpenseFormSkeleton from '@/features/dashboard/expenses/components/ExpenseFormSkeleton.tsx';

interface EditExpenseModalProps {
  tourId: string;
  expenseId: string;
  onClose: () => void;
}

/**
 * Szczegół wydatku dociąga sam modal, żeby „Edytuj” otwierało okno od razu —
 * czekanie na odpowiedź przy zamkniętym oknie wyglądało jak martwa pozycja menu.
 */
const EditExpenseModal = ({
  tourId,
  expenseId,
  onClose,
}: EditExpenseModalProps) => {
  const {
    data: expense,
    isLoading,
    isError,
  } = useGetExpense(tourId, expenseId);
  const updateMutation = useUpdateExpense(tourId, expenseId);

  const methods = useForm<ExpenseInputData>({
    resolver: zodResolver(expenseSchema),
    values: expense ? expenseMapper.fromDtoToForm(expense) : undefined,
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: ExpenseFormData) => {
    const dto = expenseMapper.toUpdateDto(data);
    updateMutation.mutate(dto, { onSuccess: () => onClose() });
  };

  return (
    <Modal onClose={onClose} isDirty={methods.formState.isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Edytuj wydatek" onClose={onClose} />
          <ModalContent>
            {isLoading ? (
              <ExpenseFormSkeleton />
            ) : isError || !expense ? (
              <LoadError subject="wydatku" />
            ) : (
              <ExpenseForm />
            )}
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={updateMutation.isPending}
              disabled={isLoading || !expense}
            >
              Zapisz zmiany
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditExpenseModal;
